import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Alert, Button, Modal, Space, Typography } from 'antd';
import { PlusOutlined, ApartmentOutlined } from '@ant-design/icons';
import { applyNodeChanges } from '@xyflow/react';
import OrganizationChart from 'components/OrganizationChart';
import { getDescendantIds, getHiddenNodeIds } from 'components/OrganizationChart/hierarchy';
import Error403 from 'components/Error/Error403';
import useTopbarTitleHooks from 'hooks/useTopbarTitleHooks';
import { APP_ROUTE_PREFIX, RouteConstant } from 'routes/routesConstant';
import LocalStorageUtils from 'utils/LocalStorage';
import RoleMenuAccess from 'utils/RoleMenuAccess';
import { backendSampleData } from './backendSampleData';
import { buildBackendChart, entityConfig, entityData } from './backendChartAdapter';
import EntityEditModal from './EntityEditModal';
import { appendEntity, childEntityTypes, createEntityNode, removeEntityBranch } from './chartActions';
import SetupModuleBanner from 'components/Onboarding/SetupModuleBanner';

const fallbackBackendChart = buildBackendChart(backendSampleData);
const MOCK_API_URL = '/mock/organization-chart.json';

export default function OrganizationChartPage() {
    useTopbarTitleHooks('Organization Chart');
    const [backendChart, setBackendChart] = useState(fallbackBackendChart);
    const [nodes, setNodes] = useState(fallbackBackendChart.nodes);
    const [edges, setEdges] = useState(fallbackBackendChart.edges);
    const [dataSourceStatus, setDataSourceStatus] = useState('loading');
    const [collapsedIds, setCollapsedIds] = useState(() => new Set());
    const [editingId, setEditingId] = useState(null);
    const [creation, setCreation] = useState(null);
    const [deletingId, setDeletingId] = useState(null);
    const [layoutVersion, setLayoutVersion] = useState(0);
    const [editingLines, setEditingLines] = useState(false);

    useEffect(() => {
        const controller = new AbortController();

        async function loadBackendSample() {
            try {
                const response = await fetch(MOCK_API_URL, {
                    signal: controller.signal,
                    cache: 'no-store',
                    headers: { Accept: 'application/json' },
                });
                if (!response.ok) throw new Error(`HTTP ${response.status}`);
                const payload = await response.json();
                if (!payload?.isValid || payload?.statusCode !== 200 || !payload?.data) {
                    throw new Error(payload?.message || 'Invalid organization chart response.');
                }

                const chart = buildBackendChart(payload.data);
                setBackendChart(chart);
                setNodes(chart.nodes);
                setEdges(chart.edges);
                setDataSourceStatus('network');
                setLayoutVersion(version => version + 1);
            } catch (error) {
                if (error.name !== 'AbortError') setDataSourceStatus('fallback');
            }
        }

        loadBackendSample();
        return () => controller.abort();
    }, []);


    const saveEntity = (id, values) => {
        if (creation) {
            const parent = nodes.find(node => node.id === creation.parentId);
            const node = createEntityNode(creation.entityType, parent, values);
            applyStructure(appendEntity(nodes, edges, node, parent?.id));
            setCreation(null);
            return;
        }
        setNodes(previous => previous.map(node => {
            if (node.id !== id) return node;
            const { entityType, record } = node.data;
            const { prefix } = entityConfig[entityType];
            const nextRecord = { ...record, [`${prefix}_name`]: values.name.trim(),
                [`${prefix}_code`]: values.code?.trim() || null };
            if (entityType === 'position') nextRecord.pos_resource_total = values.resources ?? null;
            return { ...node, data: entityData(entityType, nextRecord) };
        }));
        setEditingId(null);
    };

    const applyStructure = chart => {
        setNodes(chart.nodes);
        setEdges(chart.edges);
        setCollapsedIds(new Set());
        setLayoutVersion(version => version + 1);
    };

    const startCreation = useCallback((entityType, parent) => {
        setEditingId(null);
        setCreation({ entityType, parentId: parent?.id, parentName: parent?.data.name });
    }, []);

    const deleteEntity = () => {
        applyStructure(removeEntityBranch(nodes, edges, deletingId));
        setDeletingId(null);
    };

    const updateNodes = useCallback(changes => {
        // Update the complete collection so collapsed descendants retain their positions.
        setNodes(previous => applyNodeChanges(changes, previous));
    }, []);

    const updateRoute = useCallback((id, routePoint) => {
        setEdges(previous => previous.map(edge => edge.id === id
            ? { ...edge, data: { ...edge.data, routePoint } } : edge));
    }, []);


    const toggleBranch = useCallback(id => {
        setCollapsedIds(previous => {
            const next = new Set(previous);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }, []);

    const visibleChart = useMemo(() => {
        const hiddenIds = getHiddenNodeIds(collapsedIds, edges);
        const childCounts = new Map();
        const nodesById = new Map(nodes.map(node => [node.id, node]));
        const parentsById = new Map(edges.map(edge => [edge.target, edge.source]));
        const actionsFor = node => [
            { key: 'edit', label: 'Edit', onClick: () => setEditingId(node.id) },
            ...childEntityTypes[node.data.entityType].map(entityType => ({
                key: `add-${entityType}`,
                label: `Add child: ${entityConfig[entityType].label}`,
                onClick: () => startCreation(entityType, node),
            })),
            { key: 'sibling', label: `Add sibling: ${node.data.role}`,
                onClick: () => startCreation(node.data.entityType, nodesById.get(parentsById.get(node.id))) },
            { type: 'divider' },
            { key: 'delete', label: 'Delete', danger: true, onClick: () => setDeletingId(node.id) },
        ];
        edges.forEach(edge => childCounts.set(edge.source, (childCounts.get(edge.source) || 0) + 1));
        return {
            nodes: nodes.filter(node => !hiddenIds.has(node.id)).map(node => ({
                ...node,
                dragHandle: '.organization-chart-member__body',
                data: {
                    ...node.data,
                    childCount: childCounts.get(node.id) || 0,
                    collapsed: collapsedIds.has(node.id),
                    onEdit: setEditingId,
                    onToggle: toggleBranch,
                    actions: node.data.entityType ? actionsFor(node) : undefined,
                    actionsDisabled: dataSourceStatus === 'loading',
                },
            })),
            edges: edges.filter(edge => !hiddenIds.has(edge.source) && !hiddenIds.has(edge.target)).map(edge => ({
                ...edge,
                type: 'editableOrganization',
                data: { ...edge.data, onRouteChange: updateRoute },
            })),
        };
    }, [nodes, edges, collapsedIds, toggleBranch, updateRoute, startCreation, dataSourceStatus]);


    const roleId = LocalStorageUtils.userInfo.get()?.roleId;
    const canView = (RoleMenuAccess[roleId] || []).includes(
        `${APP_ROUTE_PREFIX}/${RouteConstant.organization.chart}`
    );

    if (!canView) return <Error403 />;

    return (
        <>
            <SetupModuleBanner />
            <Space direction="vertical" size="middle" className="organization-chart-page">
                <div className="organization-chart-toolbar">
                    <div className="organization-chart-toolbar__summary">
                        <span className="organization-chart-toolbar__icon"><ApartmentOutlined /></span>
                        <div><strong>Organization overview</strong><span>{nodes.length} units across your organization</span></div>
                    </div>
                    <Space wrap size={8}>
                        <Button onClick={() => setCollapsedIds(new Set())} disabled={!collapsedIds.size}>Expand all</Button>
                        <Button aria-pressed={editingLines} onClick={() => setEditingLines(value => !value)}>{editingLines ? 'Finish editing lines' : 'Edit lines'}</Button>
                        <Button type="primary" icon={<PlusOutlined />} onClick={() => startCreation('organization')} disabled={dataSourceStatus === 'loading'}>Add organization</Button>
                    </Space>
                     {/*<Typography.Text type="secondary">Temporary changes — reset on reload.</Typography.Text>*/}
                </div>
               
                {dataSourceStatus === 'fallback' &&
                    <Alert type="warning" showIcon message="The JSON request failed. The chart is displaying the local fallback sample." />}
                {backendChart.warnings.length > 0 &&
                    <Alert type="warning" showIcon message={backendChart.warnings.join(' ')} />}
                <div className="organization-chart-surface">
                    <div className="organization-chart-legend" aria-label="Organization levels">
                        {Object.entries(entityConfig).map(([type, config]) => (
                            <span key={type}><i style={{ background: config.color }} />{config.label}<b>{nodes.filter(node => node.data.entityType === type).length}</b></span>
                        ))}
                    </div>
                    <OrganizationChart nodes={visibleChart.nodes} edges={visibleChart.edges} onNodesChange={updateNodes} layoutVersion={layoutVersion} editingLines={editingLines} />
                    <div className="organization-chart-hint">Drag to explore · Scroll to zoom · Use the node menu to manage units</div>
                </div>
            </Space>
            <EntityEditModal node={nodes.find(node => node.id === editingId)} creation={creation}
                    onCancel={() => { setEditingId(null); setCreation(null); }} onSave={saveEntity} />
            <Modal title="Delete unit" open={!!deletingId} onCancel={() => setDeletingId(null)}
                okText="Delete" okButtonProps={{ danger: true }} onOk={deleteEntity}>
                <p>Delete <strong>{nodes.find(node => node.id === deletingId)?.data.name}</strong>?</p>
                <p>This also removes all {deletingId ? getDescendantIds(deletingId, edges).size : 0} descendant units and their connectors.</p>
            </Modal>
        </>
    );
}
